import { InvoicePosition } from '@shared/models/invoice-position';
import { TseDocument } from '@shared/models/tse/TseDocument';
import { TseDocumentPositionItem } from '@shared/models/tse/TseDocumentPositionItem';
import { getTaxRateValue } from '@core/functions/get-tax-rate-value';

const GROSS_VALUE_TOLERANCE = 0.02;

function getTseItemPositions(tseDocument: TseDocument): TseDocumentPositionItem[] {
    return (tseDocument.positions || []).filter((position): position is TseDocumentPositionItem =>
        !!position
        && typeof position === 'object'
        && (position as TseDocumentPositionItem).vatIdentification != null
        && (position as TseDocumentPositionItem).grossValue != null
    );
}

function getPositionGross(position: InvoicePosition): number {
    const totalPrice = (position.quantity ?? 1) * (position.price ?? 0) || 0;
    return Math.abs(totalPrice - (totalPrice / 100 * (position.discountPercentage ?? 0)));
}

function resolveTseVatIdentification(
    position: InvoicePosition,
    index: number,
    tseItemPositions: TseDocumentPositionItem[],
): number | null {
    const targetRate = getTaxRateValue(position);
    const targetGross = getPositionGross(position);

    const byRateAndGross = tseItemPositions.find((tsePosition) =>
        tsePosition.vatPercent === targetRate
        && Math.abs(Math.abs(tsePosition.grossValue ?? 0) - targetGross) <= GROSS_VALUE_TOLERANCE
    );
    if (byRateAndGross?.vatIdentification != null) {
        return byRateAndGross.vatIdentification;
    }

    const byIndex = tseItemPositions[index];
    if (byIndex?.vatIdentification != null && byIndex.vatPercent === targetRate) {
        return byIndex.vatIdentification;
    }

    return null;
}

/**
 * Reuses vatIdentification values from a stored TSE receipt document so cancellations
 * do not need a fresh getVatIdentification call at the Fiscal Service.
 *
 * Mutates the given `positions` in place (sets `tseVatIdentificationId` where missing).
 */
export function enrichInvoicePositionsWithTseVatIdentification(
    positions: InvoicePosition[],
    tseDocument?: TseDocument | null,
): void {
    if (!tseDocument?.positions?.length || !positions?.length) {
        return;
    }

    const tseItemPositions = getTseItemPositions(tseDocument);

    positions.forEach((position, index) => {
        if (position.tseVatIdentificationId != null) {
            return;
        }

        const vatIdentification = resolveTseVatIdentification(position, index, tseItemPositions);
        if (vatIdentification != null) {
            position.tseVatIdentificationId = vatIdentification;
        }
    });
}
